import { useState } from "react";

type Device = "desktop" | "mobile" | "text";

/**
 * Live preview.
 *
 * Rendered in a fully sandboxed iframe: the HTML is email markup — table
 * layouts and inline styles that would fight the app's own stylesheet — and a
 * template may contain hand-written HTML from the code view, which must not be
 * able to run script or reach into the page around it.
 */
export function Preview({
  html,
  text,
  subject,
  previewText,
  from,
}: {
  html: string;
  text: string;
  /** Merged against nobody, so a missing fallback is visible here first. */
  subject?: string | undefined;
  previewText?: string | null | undefined;
  from?: string | undefined;
}) {
  const [device, setDevice] = useState<Device>("desktop");

  return (
    <div className="preview">
      <div className="preview-head">
        <h3>Preview</h3>
        <div className="tabs">
          {(["desktop", "mobile", "text"] as Device[]).map((d) => (
            <button
              key={d}
              type="button"
              className={device === d ? "active" : ""}
              onClick={() => setDevice(d)}
            >
              {d === "text" ? "Plain text" : d}
            </button>
          ))}
        </div>
      </div>

      {/*
        The inbox line. Everything below it only gets read if this works, and
        until now the composer previewed the body and not the one part of the
        message every recipient sees.
      */}
      {subject !== undefined && (
        <div className="inbox-line">
          {from && <div className="inbox-from">{from}</div>}
          <div className="inbox-subject">
            {subject.trim() || <span className="inbox-empty">No subject</span>}
          </div>
          <div className="inbox-preview">
            {previewText?.trim() || <span className="inbox-empty">No preview text</span>}
          </div>
        </div>
      )}

      {device === "text" ? (
        <pre className="preview-text">{text || "Nothing to show yet."}</pre>
      ) : (
        <div className={`preview-frame ${device}`}>
          {/*
            srcdoc rather than writing into contentDocument: sandbox="" gives the
            frame an opaque origin, which is the point — hand-written HTML from
            the code view must not reach the page around it — but that also makes
            contentDocument unreachable from here.
          */}
          <iframe title="Email preview" sandbox="" srcDoc={html} />
        </div>
      )}
    </div>
  );
}
